import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Avatar,
  Box,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { InsertPhotoOutlined } from '@mui/icons-material';
import CustomPaper from '../common/CustomPaper';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import FormMentionEditor from '../Component/FormMentionEditor';
import ChipInput from '../ChipInput/ChipInput';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import { getCurrentTime } from '../../utils/getCurrentTime';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import { stringAvatar } from '../../utils/AvatarUtils';
import AgentAvatar from '../pages/AgentAvatar';
import useAIAgentStore, { getAIAgentStore,
  DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY,
  isDefaultOutputPlaceholder,
} from './useAIAgentStore';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import '../Organization/OrganizationSettings.css';

const MODEL_LABELS = {
  'gpt-4o': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o Mini',
  'gpt-4.1': 'GPT-4.1',
  'gpt-4.1-mini': 'GPT-4.1 Mini',
  'o3-mini': 'o3-mini',
  'claude-sonnet-4-20250514': 'Claude Sonnet 4',
  'claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet',
  'claude-3-7-sonnet-20250219': 'Claude 3.7 Sonnet',
  'gemini-2.0-flash': 'Gemini 2.0 Flash',
};

const AIAgentTemplate = (props) => {
  const latestSessionLookupAgentIdRef = useRef(null);

  const store = useMemo(() => getAIAgentStore(props.component_id), [props.component_id]);

  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [recentChatSessions, setRecentChatSessions] = useState([]);
  const [recentSessionsLoading, setRecentSessionsLoading] = useState(false);

  const getChatSessionId = (option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return String(option.chatSessionId || option.sessionId || '');
  };

  const formatChatSessionTimestamp = (option) => {
    const dateValue = option?.updatedAt || option?.createdAt;
    if (!dateValue) return '';
    const timestamp = new Date(dateValue).getTime();
    if (!Number.isFinite(timestamp) || timestamp <= 0) return '';
    return new Date(timestamp).toLocaleString();
  };

  const fetchRecentChatSessions = async (agentId) => {
    const lookupAgentId = String(agentId || '');
    latestSessionLookupAgentIdRef.current = lookupAgentId;

    if (!lookupAgentId) {
      setRecentChatSessions([]);
      setRecentSessionsLoading(false);
      return;
    }

    setRecentSessionsLoading(true);
    try {
      const response = await send_request(
        `project-service/agent/${lookupAgentId}/sessions`,
        '',
        {},
        'GET'
      );
      if (latestSessionLookupAgentIdRef.current !== lookupAgentId) return;

      const rawSessions = Array.isArray(response?.data) ? response.data : [];
      const seenSessionIds = new Set();
      const normalizedSessions = rawSessions
        .map((session) => {
          if (typeof session === 'string') {
            return { chatSessionId: session };
          }
          if (!session || typeof session !== 'object') {
            return null;
          }
          return {
            ...session,
            chatSessionId: String(session.chatSessionId || session.sessionId || ''),
          };
        })
        .filter((session) => {
          const sessionId = getChatSessionId(session);
          if (!sessionId || seenSessionIds.has(sessionId)) return false;
          seenSessionIds.add(sessionId);
          return true;
        })
        .sort((a, b) => {
          const aTimestamp = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
          const bTimestamp = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
          const safeA = Number.isFinite(aTimestamp) ? aTimestamp : 0;
          const safeB = Number.isFinite(bTimestamp) ? bTimestamp : 0;
          return safeB - safeA;
        })
        .slice(0, 50);

      setRecentChatSessions(normalizedSessions);
    } catch (_error) {
      if (latestSessionLookupAgentIdRef.current !== lookupAgentId) return;
      setRecentChatSessions([]);
    } finally {
      if (latestSessionLookupAgentIdRef.current === lookupAgentId) {
        setRecentSessionsLoading(false);
      }
    }
  };

  const keyAuto = () => {
    const rand = (Math.floor(Math.random() * 90000000) + 10000).toString();
    return 'agent' + rand;
  };

  // The AI Agent store keeps `outputPlaceholders` at the flat root of
  // `data` (`useAIAgentStore.onLoadData` explicitly flattens any v1
  // `aiAgentData` nesting). Earlier reads via `data.aiAgentData.X` always
  // returned undefined, so the rename UI silently rendered an empty list
  // — fixed here so authors can actually edit the renameable system
  // outputs (chatSessionId + agentSessionUrl).
  const addPlaceholder = () => {
    let placeholders = JSON.parse(
      JSON.stringify(store.getState().data?.outputPlaceholders || [])
    );
    placeholders.push({ key: keyAuto(), type: 'text', desc: '' });
    store.getState().setOutputPlaceholders(placeholders);
  };

  const deletePlaceholder = (index) => {
    let placeholders = JSON.parse(
      JSON.stringify(store.getState().data?.outputPlaceholders || [])
    );
    if (isDefaultOutputPlaceholder(placeholders[index])) return;
    placeholders.splice(index, 1);
    store.getState().setOutputPlaceholders(placeholders);
  };

  const setPlaceholderKey = (index, value) => {
    let placeholders = JSON.parse(
      JSON.stringify(store.getState().data?.outputPlaceholders || [])
    );
    placeholders[index].key = replacePlaceholderValue(value);
    store.getState().setOutputPlaceholders(placeholders);
  };

  const setPlaceholderDesc = (index, value) => {
    let placeholders = JSON.parse(
      JSON.stringify(store.getState().data?.outputPlaceholders || [])
    );
    placeholders[index].desc = value;
    store.getState().setOutputPlaceholders(placeholders);
  };

  const handleAgentChange = (_, agent) => {
    setSelectedAgent(agent);
    setCopiedEmail(false);
    store.getState().setAgentId(agent ? agent.agentId : null);
    fetchRecentChatSessions(agent?.agentId || null);
  };

  const handleCopyEmail = (e) => {
    e.stopPropagation();
    const email = selectedAgent?.inboxAddress;
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const handleChatSessionSelectionChange = (_, newValue) => {
    store.getState().setChatSessionId(getChatSessionId(newValue));
  };

  const handleChatSessionInputChange = (_, newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
      store.getState().setChatSessionId(newInputValue || '');
    }
  };

  useEffect(() => {
    if (!props.component_id) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoading(false);
      props.setChildTemplateLoaded(true);

      // Still need to fetch agents list even when component data is loaded
      const loadAgents = async () => {
        try {
          const agentsRes = await send_request('project-service/agent/list', '', {}, 'GET');
          const agentsList = agentsRes?.data || [];
          const currentAgentId = store.getState().data?.aiAgentData?.agentId;
          const found = currentAgentId
            ? agentsList.find((a) => a.agentId === currentAgentId) || null
            : null;
          setAgents(agentsList);
          setSelectedAgent(found);
          const selectedAgentId = found?.agentId || currentAgentId || null;
          fetchRecentChatSessions(selectedAgentId);
        } catch (_e) { /* ignore */ }
      };
      loadAgents();
      return;
    }

    const loadData = async () => {
      try {
        const [componentRes, agentsRes] = await Promise.all([
          send_request(
            `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
            '',
            {}
          ),
          send_request('project-service/agent/list', '', {}, 'GET'),
        ]);

        const component = componentRes?.data?.components?.[0] || componentRes?.data;
        if (component) {
          store.getState().setComponentData(component);
          store.setState({ loaded: true });
          props.onComponentNameChanged(store.getState().name);
          props.onLastModifiedChanged(store.getState().lastModified);
        }

        const agentsList = agentsRes?.data || [];
        const currentAgentId = store.getState().data?.aiAgentData?.agentId;
        const found = currentAgentId
          ? agentsList.find((a) => a.agentId === currentAgentId) || null
          : null;

        setAgents(agentsList);
        setSelectedAgent(found);
        const selectedAgentId = found?.agentId || currentAgentId || null;
        fetchRecentChatSessions(selectedAgentId);
      } finally {
        setLoading(false);
        props.setChildTemplateLoaded(true);
      }
    };

    loadData();
  }, []);

  const agentData = store((s) => s.data?.aiAgentData) || {};
  // outputPlaceholders is kept flat at `data.outputPlaceholders` by the
  // Zustand store (see useAIAgentStore.onLoadData which explicitly
  // flattens any v1 aiAgentData wrapper). Read from the flat path so
  // the rename UI for the system entries (chatSessionId, agentSessionUrl)
  // actually renders.
  const outputPlaceholders = store((s) => s.data?.outputPlaceholders) || [];
  const availablePlaceholders = props.availablePlaceholders;
  const hasAgentForSessionLookup = Boolean(
    selectedAgent?.agentId || agentData.agentId
  );

  if (loading) return null;

  return (
    <Grid container direction="column">
      <CustomPaper>
        <Grid item xs={12} style={{ paddingBottom: '16px' }}>
          <span>
            <b>Agent</b>
          </span>
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            options={agents}
            value={selectedAgent}
            onChange={handleAgentChange}
            getOptionLabel={(option) => option.name || 'Untitled'}
            isOptionEqualToValue={(option, value) => option.agentId === value?.agentId}
            renderOption={(renderProps, option) => {
              const modelLabel = MODEL_LABELS[option.model] || option.model || 'No model';
              const toolCount = option.enabledTools ? option.enabledTools.length : 0;
              return (
                <li {...renderProps} key={option.agentId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', py: 0.5 }}>
                    <AgentAvatar agent={option} name={option.name} size={32} borderRadius="8px" />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }} noWrap>
                        {option.name || 'Untitled Agent'}
                      </Typography>
                      <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', lineHeight: 1.3 }} noWrap>
                        {option.description || 'No description'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                        <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
                          {modelLabel}
                        </Typography>
                        <Typography sx={{ fontSize: '10.5px', color: 'divider' }}>·</Typography>
                        <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
                          {toolCount > 0 ? `${toolCount} tool${toolCount !== 1 ? 's' : ''}` : 'No tools'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                className="org-styled-textfield"
                placeholder="Select an agent"
                variant="outlined"
                size="small"
              />
            )}
            sx={{
              '& .MuiAutocomplete-popupIndicator': { color: 'text.secondary' },
            }}
            componentsProps={{
              paper: {
                sx: { borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', mt: 0.5 },
              },
            }}
          />
        </Grid>

        {selectedAgent && (() => {
          const agent = selectedAgent;
          const modelLabel = MODEL_LABELS[agent.model] || agent.model || 'No model';
          const toolCount = agent.enabledTools ? agent.enabledTools.length : 0;
          const accentColor = stringAvatar(agent.name || 'Agent').sx?.bgcolor || '#71717a';
          const shortId = agent.agentId ? agent.agentId.slice(0, 8).toUpperCase() : '';

          return (
            <Box
              sx={{
                mt: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: 'background.paper',
              }}
            >
              <Box sx={{ height: '3px', width: '100%', backgroundColor: accentColor }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, pt: 2, pb: 1.5 }}>
                <AgentAvatar agent={agent} name={agent.name} size={40} borderRadius="10px" sx={{ boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }} noWrap>
                    {agent.name || 'Untitled Agent'}
                  </Typography>
                  {agent.description ? (
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }}>
                      {agent.description}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: '12px', color: 'divider', fontStyle: 'italic', mt: 0.25 }}>
                      No description
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mx: 2, borderColor: 'action.hover' }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 2, py: 1.5 }}>
                {agent.inboxAddress ? (
                  <Tooltip title={copiedEmail ? 'Copied!' : 'Click to copy'} placement="bottom-start">
                    <Box
                      onClick={handleCopyEmail}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        px: 1,
                        py: 0.5,
                        mx: -1,
                        borderRadius: '6px',
                        border: '1px solid transparent',
                        transition: 'all 150ms ease',
                        ...(copiedEmail
                          ? { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }
                          : { '&:hover': { backgroundColor: 'action.hover', borderColor: 'divider' } }),
                      }}
                    >
                      <EmailOutlinedIcon sx={{ fontSize: 15, color: copiedEmail ? '#15803d' : 'text.disabled', flexShrink: 0 }} />
                      <Typography
                        sx={{ fontSize: '12px', color: copiedEmail ? '#15803d' : 'text.secondary', flex: 1 }}
                        noWrap
                      >
                        {agent.inboxAddress}
                      </Typography>
                      {copiedEmail ? (
                        <CheckIcon sx={{ fontSize: 13, color: '#15803d', flexShrink: 0 }} />
                      ) : (
                        <ContentCopyIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0, opacity: 0, '.MuiBox-root:hover &': { opacity: 1 } }} />
                      )}
                    </Box>
                  </Tooltip>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12px', color: 'divider', fontStyle: 'italic' }}>
                      No inbox assigned
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmartToyOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{modelLabel}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BuildOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {toolCount > 0 ? `${toolCount} tool${toolCount !== 1 ? 's' : ''} enabled` : 'No tools enabled'}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: 'action.hover',
                  borderTop: '1px solid',
                  borderTopColor: 'action.hover',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontFamily: "'SF Mono', 'Menlo', 'Monaco', monospace",
                    color: 'divider',
                    letterSpacing: '0.5px',
                  }}
                >
                  ID: {shortId}
                </Typography>
              </Box>
            </Box>
          );
        })()}
      </CustomPaper>

      <CustomPaper style={{ marginTop: '16px' }}>
        <Grid item xs={12} style={{ paddingBottom: '12px' }}>
          <span>
            <b>Chat session</b>
          </span>
        </Grid>
        <RadioGroup
          value={agentData.sessionMode || 'new'}
          onChange={(e) => store.getState().setSessionMode(e.target.value)}
        >
          <FormControlLabel
            value="new"
            control={
              <Radio
                size="small"
                sx={{ color: 'text.disabled', '&.Mui-checked': { color: 'text.primary' } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                Create a new chat session
              </Typography>
            }
          />
          <FormControlLabel
            value="existing"
            control={
              <Radio
                size="small"
                sx={{ color: 'text.disabled', '&.Mui-checked': { color: 'text.primary' } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                Send message to an existing chat session
              </Typography>
            }
          />
        </RadioGroup>
        {(agentData.sessionMode === 'existing') && (
          <Box sx={{ mt: 1.5 }}>
            <Typography className="org-field-label">Chat session ID</Typography>
            <Autocomplete
              freeSolo
              options={recentChatSessions}
              loading={recentSessionsLoading}
              value={agentData.chatSessionId || ''}
              inputValue={agentData.chatSessionId || ''}
              onChange={handleChatSessionSelectionChange}
              onInputChange={handleChatSessionInputChange}
              getOptionLabel={(option) => getChatSessionId(option)}
              isOptionEqualToValue={(option, value) => {
                return getChatSessionId(option) === getChatSessionId(value);
              }}
              noOptionsText={
                hasAgentForSessionLookup
                  ? 'No recent sessions found'
                  : 'Select an agent to load recent sessions'
              }
              renderOption={(renderProps, option) => {
                const sessionId = getChatSessionId(option);
                const sessionPreview = option?.title || '';
                const timestampLabel = formatChatSessionTimestamp(option);
                const metadata = [sessionPreview, timestampLabel].filter(Boolean).join(' • ');
                return (
                  <li {...renderProps} key={sessionId}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                      <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>
                        {sessionId}
                      </Typography>
                      {metadata && (
                        <Typography
                          sx={{ fontSize: '11.5px', color: 'text.secondary', lineHeight: 1.3 }}
                          noWrap
                        >
                          {metadata}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="org-styled-textfield"
                  placeholder="Search recent sessions or enter any session ID"
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Box>
        )}
      </CustomPaper>

      <CustomPaper style={{ marginTop: '16px' }}>
        <Grid item xs={12} style={{ paddingBottom: '16px' }}>
          <span>
            <b>Message</b>
          </span>
        </Grid>
        <Grid item xs={12}>
          <FormMentionEditor
            availablePlaceholders={
              availablePlaceholders != null ? availablePlaceholders[0].placeholders : []
            }
            text={agentData.message || ''}
            onChange={(html) => store.getState().setMessage(html)}
            placeholder=""
            aiAssistant={true}
            style={{
              width: '100%',
              verticalAlign: 'top',
              minHeight: '16px',
              fontSize: '14px',
            }}
            onFocus={() => {}}
            contentBlock={false}
            hideScrollBar={true}
            assignTask={true}
          />
        </Grid>
      </CustomPaper>

      <CustomPaper style={{ marginTop: '16px' }}>
        <Grid container alignItems="center" style={{ paddingBottom: '8px' }}>
          <InsertPhotoOutlined style={{ marginRight: 6, fontSize: 20, color: 'var(--mui-palette-text-secondary)' }} />
          <span>
            <b>Image input</b>
          </span>
        </Grid>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1.5 }}>
          Add image URLs for the agent to use. Supports .png, .jpg, .jpeg format.
        </Typography>
        <Grid item xs={12}>
          <ChipInput
            inputValue={agentData.imageContext || []}
            onPlaceholderSelected={props.onPlaceholderSelected}
            placeholders={availablePlaceholders}
            placeholder="Select placeholders"
            fullWidth
            onDelete={(index) => store.getState().deleteImageChip(index)}
          />
        </Grid>
      </CustomPaper>

      <CustomPaper style={{ marginTop: '16px' }}>
        <Grid item xs={12} style={{ paddingBottom: '12px' }}>
          <span>
            <b>Output</b>
          </span>
        </Grid>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 2 }}>
          Define the structured outputs this agent should return.
        </Typography>

        {outputPlaceholders.map((placeholder, idx) => {
          const isDefault = isDefaultOutputPlaceholder(placeholder);
          return (
            <Box
              key={idx}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                p: 2,
                mb: 1.5,
                position: 'relative',
                backgroundColor: 'background.paper',
                transition: 'border-color 0.15s ease',
                '&:hover': {
                  borderColor: 'divider',
                },
              }}
            >
              {!isDefault && (
                <IconButton
                  size="small"
                  onClick={() => deletePlaceholder(idx)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: 'text.disabled',
                    '&:hover': { color: 'text.secondary', backgroundColor: 'action.hover' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}

              <Box sx={{ mb: 1.5 }}>
                <Typography className="org-field-label">Description</Typography>
                <FormMentionEditor
                  availablePlaceholders={
                    availablePlaceholders != null ? availablePlaceholders[0].placeholders : []
                  }
                  text={placeholder.desc || ''}
                  onChange={(html) => setPlaceholderDesc(idx, html)}
                  placeholder=""
                  aiAssistant={true}
                  style={{
                    width: '100%',
                    verticalAlign: 'top',
                    minHeight: '16px',
                    fontSize: '14px',
                    background: 'var(--mui-palette-background-paper)',
                  }}
                  onFocus={() => {}}
                  contentBlock={false}
                  hideScrollBar={true}
                  assignTask={true}
                />
              </Box>

              <Box>
                <Typography className="org-field-label">Placeholder</Typography>
                <W86PlaceholderTextField
                  value={placeholder.key || ''}
                  fullWidth
                  handleChange={(e) => setPlaceholderKey(idx, e.target.value)}
                  placeholderText="Enter placeholder name"
                />
                {isDefault && (
                  <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.75 }}>
                    Required system output (cannot be removed).
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}

        <Box
          onClick={addPlaceholder}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            py: 1,
            px: 0.5,
            borderRadius: '6px',
            color: 'text.secondary',
            transition: 'color 0.15s ease',
            '&:hover': {
              color: 'text.primary',
            },
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>Add output</Typography>
        </Box>
      </CustomPaper>
    </Grid>
  );
};

export default AIAgentTemplate;
